import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import { Send, Add } from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface Message {
  id: string;
  recipient_id: string;
  subject: string;
  content: string;
  sender_name: string;
  sender_email: string | null;
  is_read: boolean;
  read_at: string | null;
  sent_at: string;
  created_by: string | null;
}

interface Alumni {
  _id: string;
  first_name_th: string;
  last_name_th: string;
  user_id: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MessageManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [alumni, setAlumni] = useState<Alumni[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Send message dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendType, setSendType] = useState<'individual' | 'bulk'>('individual');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [senderName, setSenderName] = useState('ระบบศิษย์เก่า');
  const [senderEmail, setSenderEmail] = useState('');

  useEffect(() => {
    fetchMessages();
    fetchAlumni();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/messages/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อความ');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/alumni`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 },
      });
      // Filter alumni with LINE linkage
      const linkedAlumni = response.data.data.filter((a: Alumni) => a.user_id);
      setAlumni(linkedAlumni);
    } catch (err) {
      console.error('Failed to fetch alumni:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!subject || !content) {
      setError('กรุณากรอกหัวข้อและเนื้อหาข้อความ');
      return;
    }

    if (sendType === 'individual' && !selectedRecipient) {
      setError('กรุณาเลือกผู้รับ');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const token = localStorage.getItem('token');

      if (sendType === 'bulk') {
        await axios.post(
          `${API_URL}/api/v1/messages/admin/send-bulk`,
          {
            subject,
            content,
            sender_name: senderName,
            sender_email: senderEmail || undefined,
            send_to_all: true,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(`ส่งข้อความถึงศิษย์เก่าทั้งหมดสำเร็จ`);
      } else {
        await axios.post(
          `${API_URL}/api/v1/messages/admin/send`,
          {
            recipient_id: selectedRecipient,
            subject,
            content,
            sender_name: senderName,
            sender_email: senderEmail || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const recipient = alumni.find(a => a.user_id === selectedRecipient);
        setSuccess(`ส่งข้อความถึง ${recipient?.first_name_th} ${recipient?.last_name_th} สำเร็จ`);
      }

      // Reset form
      setSendDialogOpen(false);
      setSelectedRecipient('');
      setSubject('');
      setContent('');
      setSenderName('ระบบศิษย์เก่า');
      setSenderEmail('');
      
      // Refresh messages
      fetchMessages();
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setError(err.response?.data?.detail || 'เกิดข้อผิดพลาดในการส่งข้อความ');
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getRecipientName = (recipient_id: string) => {
    const alumnus = alumni.find(a => a.user_id === recipient_id);
    if (alumnus) {
      return `${alumnus.first_name_th} ${alumnus.last_name_th}`;
    }
    return recipient_id;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          จัดการข้อความ
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setSendDialogOpen(true)}
          sx={{ bgcolor: 'primary.main' }}
        >
          ส่งข้อความใหม่
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={`ข้อความทั้งหมด (${messages.length})`} />
          <Tab label={`อ่านแล้ว (${messages.filter(m => m.is_read).length})`} />
          <Tab label={`ยังไม่ได้อ่าน (${messages.filter(m => !m.is_read).length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ผู้รับ</TableCell>
                  <TableCell>หัวข้อ</TableCell>
                  <TableCell>ผู้ส่ง</TableCell>
                  <TableCell>วันที่ส่ง</TableCell>
                  <TableCell>สถานะ</TableCell>
                  <TableCell>วันที่อ่าน</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell>{getRecipientName(message.recipient_id)}</TableCell>
                    <TableCell>{message.subject}</TableCell>
                    <TableCell>{message.sender_name}</TableCell>
                    <TableCell>{formatDateTime(message.sent_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={message.is_read ? 'อ่านแล้ว' : 'ยังไม่ได้อ่าน'}
                        color={message.is_read ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {message.read_at ? formatDateTime(message.read_at) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {messages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">ไม่มีข้อความ</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ผู้รับ</TableCell>
                  <TableCell>หัวข้อ</TableCell>
                  <TableCell>ผู้ส่ง</TableCell>
                  <TableCell>วันที่ส่ง</TableCell>
                  <TableCell>วันที่อ่าน</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.filter(m => m.is_read).map((message) => (
                  <TableRow key={message.id}>
                    <TableCell>{getRecipientName(message.recipient_id)}</TableCell>
                    <TableCell>{message.subject}</TableCell>
                    <TableCell>{message.sender_name}</TableCell>
                    <TableCell>{formatDateTime(message.sent_at)}</TableCell>
                    <TableCell>{message.read_at ? formatDateTime(message.read_at) : '-'}</TableCell>
                  </TableRow>
                ))}
                {messages.filter(m => m.is_read).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">ไม่มีข้อความที่อ่านแล้ว</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ผู้รับ</TableCell>
                  <TableCell>หัวข้อ</TableCell>
                  <TableCell>ผู้ส่ง</TableCell>
                  <TableCell>วันที่ส่ง</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.filter(m => !m.is_read).map((message) => (
                  <TableRow key={message.id}>
                    <TableCell>{getRecipientName(message.recipient_id)}</TableCell>
                    <TableCell>{message.subject}</TableCell>
                    <TableCell>{message.sender_name}</TableCell>
                    <TableCell>{formatDateTime(message.sent_at)}</TableCell>
                  </TableRow>
                ))}
                {messages.filter(m => !m.is_read).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">ไม่มีข้อความที่ยังไม่ได้อ่าน</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Send Message Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>ส่งข้อความใหม่</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>ประเภทการส่ง</InputLabel>
                <Select
                  value={sendType}
                  label="ประเภทการส่ง"
                  onChange={(e) => setSendType(e.target.value as 'individual' | 'bulk')}
                >
                  <MenuItem value="individual">ส่งถึงบุคคล</MenuItem>
                  <MenuItem value="bulk">ส่งถึงทุกคน</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {sendType === 'individual' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>ผู้รับ</InputLabel>
                  <Select
                    value={selectedRecipient}
                    label="ผู้รับ"
                    onChange={(e) => setSelectedRecipient(e.target.value)}
                  >
                    {alumni.map((alumnus) => (
                      <MenuItem key={alumnus._id} value={alumnus.user_id!}>
                        {alumnus.first_name_th} {alumnus.last_name_th}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="หัวข้อ"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="เนื้อหา"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                rows={6}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ชื่อผู้ส่ง"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="อีเมลผู้ส่ง (ไม่บังคับ)"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                type="email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>ยกเลิก</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
            disabled={sending}
          >
            ส่งข้อความ
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MessageManagement;
